import { MatrixClient, Room } from 'matrix-js-sdk/src/matrix';

import { getAllMessengerRooms } from 'modules/Messenger/components/ConversationsList/utils';
import logger from 'utils/logger';
import { isSystemUser } from 'utils/matrixUtils';
import { getCanonicalParentRoom } from 'utils/roomHierarchyUtils';

/**
 * Returns any existing Messenger group rooms that have exactly the same joined member set
 * as the selected participants + the current user.
 *
 * @param matrixClient - The Matrix client instance
 * @param selectedMxids - Participant MXIDs selected by the user (does NOT include the current user)
 * @param workspaceId - When set, only rooms whose canonical parent space is this workspace are
 *   considered; non-workspace and cross-workspace rooms are filtered out before the member-set
 *   comparison. When null/undefined (personal user, no workspace), all messenger rooms are considered.
 */
export const findConflictingGroupChats = (
  matrixClient: MatrixClient,
  selectedMxids: string[],
  workspaceId?: string | null
): Room[] => {
  try {
    const currentUserId = matrixClient.getUserId();
    const expected = new Set<string>([...selectedMxids, ...(currentUserId ? [currentUserId] : [])]);

    return getAllMessengerRooms(matrixClient).filter((room) => {
      if (workspaceId && getCanonicalParentRoom(room)?.roomId !== workspaceId) return false;

      const joined = room
        .getJoinedMembers()
        .filter((m) => !isSystemUser(m.userId))
        .map((m) => m.userId);

      if (joined.length !== expected.size) return false;

      return joined.every((id) => expected.has(id));
    });
  } catch (error) {
    logger.warn('findConflictingGroupChats: error scanning rooms', { error });
    return [];
  }
};
